import { useState, useEffect } from 'react';
import { Upload, Instagram, Mail, User, FileText, X, CheckCircle, Tag, Video } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModelSignUp = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    instagram_url: '',
    bio: ''
  });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [editToken, setEditToken] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/models/categories`);
      setAvailableCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback categories
      setAvailableCategories(['Fashion', 'Boudoir', 'Editorial', 'Commercial', 'Fitness', 'Alternative', 'Glamour', 'Art Nude']);
    }
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length + images.length > 4) {
      toast({
        title: "Too many images",
        description: "You can upload a maximum of 4 images.",
        variant: "destructive",
      });
      return;
    }

    // Validate file types
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    if (validFiles.length !== files.length) {
      toast({
        title: "Invalid file type",
        description: "Please upload only image files.",
        variant: "destructive",
      });
    }

    setImages(prev => [...prev, ...validFiles]);
    
    // Create previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate video type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid video format",
        description: "Please upload MP4, WebM, MOV, or AVI files only.",
        variant: "destructive",
      });
      return;
    }

    // Check file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      toast({
        title: "Video too large",
        description: "Video must be under 50MB.",
        variant: "destructive",
      });
      return;
    }

    setVideo(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const removeVideo = () => {
    setVideo(null);
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
      setVideoPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (images.length < 1) {
      toast({
        title: "Images required",
        description: "Please upload at least 1 image (up to 4).",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('instagram_url', formData.instagram_url);
      formDataToSend.append('bio', formData.bio);
      formDataToSend.append('categories', selectedCategories.join(','));
      
      images.forEach((image) => {
        formDataToSend.append('images', image);
      });

      if (video) {
        formDataToSend.append('video', video);
      }

      const response = await axios.post(`${API}/models/signup`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 'success') {
        setEditToken(response.data.edit_token);
        setSubmitted(true);
        toast({
          title: "Application Submitted!",
          description: "We'll review your application and get back to you soon.",
        });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      const message = error.response?.data?.detail || "Failed to submit application. Please try again.";
      toast({
        title: "Submission Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-black text-white min-h-screen pt-32 pb-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-green-600/20 flex items-center justify-center">
              <CheckCircle className="text-green-500" size={40} />
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-6">
              Application Submitted!
            </h1>
            <p className="text-xl text-gray-300 mb-4">
              Thank you for your interest in joining EWS Productions as a freelance model.
              Our team will review your application and contact you soon.
            </p>
            {editToken && (
              <div className="bg-zinc-900 rounded-lg p-4 mb-8 border border-red-900/20 text-left">
                <p className="text-sm text-gray-400 mb-2">Save this link to edit your profile later:</p>
                <code className="text-xs text-red-400 break-all block bg-black p-2 rounded">
                  {window.location.origin}/edit-profile/{editToken}
                </code>
              </div>
            )}
            <a 
              href="/"
              className="inline-block bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
            >
              Return Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-red-500 mb-6 uppercase">
            Model Sign Up
          </h1>
          <p className="text-lg text-gray-300 leading-relaxed mb-4">
            Join our directory of freelance models. Complete the form below to submit your application.
            All applications are reviewed by our team before approval.
          </p>
          <div className="bg-zinc-900/50 rounded-lg p-4 border border-red-900/20 inline-block">
            <p className="text-green-400 font-semibold mb-1">✓ Free to Join</p>
            <p className="text-gray-400 text-sm">
              If you get booked through our directory, we'd appreciate a small donation – 
              <a 
                href="https://buymeacoffee.com/ewsproductions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-400 hover:text-red-300 underline transition-colors"
              >
                buy us a coffee
              </a> to help keep EWS running!
            </p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="bg-zinc-900 rounded-lg p-8 border border-red-900/20">
            {/* Name */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <User size={16} /> Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Your full name"
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                data-testid="model-signup-name"
              />
            </div>

            {/* Email */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Mail size={16} /> Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="your@email.com"
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                data-testid="model-signup-email"
              />
            </div>

            {/* Instagram */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Instagram size={16} /> Instagram URL
              </label>
              <input
                type="url"
                name="instagram_url"
                value={formData.instagram_url}
                onChange={handleChange}
                placeholder="https://instagram.com/yourprofile"
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors"
                data-testid="model-signup-instagram"
              />
            </div>

            {/* Bio */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <FileText size={16} /> Bio / About You
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                rows={4}
                placeholder="Tell us about yourself, your experience, and what type of work you're interested in..."
                className="w-full bg-black border border-red-900/30 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500 transition-colors resize-none"
                data-testid="model-signup-bio"
              />
            </div>

            {/* Categories */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Tag size={16} /> Categories (Select all that apply)
              </label>
              <div className="flex flex-wrap gap-2" data-testid="category-selector">
                {availableCategories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => toggleCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm transition-all ${
                      selectedCategories.includes(category)
                        ? 'bg-red-600 text-white'
                        : 'bg-zinc-800 text-gray-400 hover:bg-zinc-700 hover:text-white'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
              {selectedCategories.length > 0 && (
                <p className="text-gray-500 text-xs mt-2">
                  Selected: {selectedCategories.join(', ')}
                </p>
              )}
            </div>

            {/* Image Upload */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Upload size={16} /> Portfolio Images * (1-4 images)
              </label>
              
              {/* Preview Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {previews.map((preview, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-red-900/30">
                    <img 
                      src={preview} 
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 w-6 h-6 bg-black/80 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                
                {/* Add more button */}
                {previews.length < 4 && (
                  <label className="aspect-square rounded-lg border-2 border-dashed border-red-900/30 hover:border-red-500 transition-colors cursor-pointer flex flex-col items-center justify-center text-gray-500 hover:text-red-400">
                    <Upload size={24} className="mb-2" />
                    <span className="text-xs text-center px-2">Add Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      multiple
                      data-testid="model-signup-images"
                    />
                  </label>
                )}
              </div>
              
              <p className="text-gray-500 text-xs">
                Upload high-quality images that showcase your modeling portfolio. Supported formats: JPG, PNG, WebP.
              </p>
            </div>

            {/* Video Upload */}
            <div className="mb-8">
              <label className="flex items-center gap-2 text-sm font-mono text-gray-400 mb-2 tracking-wider uppercase">
                <Video size={16} /> Portfolio Video (Optional)
              </label>
              
              {videoPreview ? (
                <div className="relative rounded-lg overflow-hidden border border-red-900/30 bg-black">
                  <video 
                    src={videoPreview}
                    controls
                    className="w-full max-h-64 object-contain"
                  />
                  <button
                    type="button"
                    onClick={removeVideo}
                    className="absolute top-2 right-2 w-8 h-8 bg-black/80 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <label className="block w-full p-8 rounded-lg border-2 border-dashed border-red-900/30 hover:border-red-500 transition-colors cursor-pointer text-center">
                  <Video size={32} className="mx-auto mb-3 text-gray-500" />
                  <span className="text-gray-400 block mb-1">Click to upload a video</span>
                  <span className="text-gray-600 text-xs">MP4, WebM, MOV, AVI (max 50MB)</span>
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                    onChange={handleVideoChange}
                    className="hidden"
                    data-testid="model-signup-video"
                  />
                </label>
              )}
              
              <p className="text-gray-500 text-xs mt-2">
                A short video showcasing your walk, poses, or previous work can help you stand out.
              </p>
            </div>

            {/* Terms */}
            <div className="mb-8 p-4 bg-black/50 rounded-lg border border-red-900/20">
              <p className="text-gray-400 text-sm">
                By submitting this application, you confirm that:
              </p>
              <ul className="text-gray-500 text-xs mt-2 space-y-1">
                <li>• You are 18 years or older</li>
                <li>• You have the right to use the images uploaded</li>
                <li>• You agree to our terms of service and privacy policy</li>
              </ul>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all flex items-center justify-center gap-2"
              data-testid="model-signup-submit"
            >
              {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ModelSignUp;
